import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Divider, Dialog, Grid } from "@mui/material";

import SoftBox from "../../components/SoftBox";
import SoftTypography from "../../components/SoftTypography";
import SoftButton from "../../components/SoftButton";
import SoftSelect from "../../components/SoftSelect";
import ChallengeBox from "../../components/ChallengeArena/ChallengeBox";
import CreateChallenge from "../../components/ChallengeArena/CreateChallenge";
import ChallengeConfirmation from "../../components/ChallengeArena/ChallengeConfirmation";
import { GET_ONE_VS_ONE_CHALLENGE } from "../../api/challenge";
import { UseCompanyId, UseUserDetails, UseUserId } from "../../hooks/auth";
import LoadingScreen from "../../components/LoadingScreen";
import GoogleBusinessModal from "../../components/ChallengeArena/GoogleBusinessModal";
import {
  GET_APP_INTEGRATIONS,
  GET_COMPANY_INTEGRATIONS,
  GET_KPI_INTEGRATIONS_BY_DATASET,
} from "../../api/integration";
import { GOOGLE_BUSINESS } from "../../constants/integrations";
import { updateWorkspaceKpi } from "../../redux/kpi/actions";
import { UseSelectedWorkspace } from "../../hooks/company";
import useMediaQuery from "@mui/material/useMediaQuery";
import ContactPageIcon from "@mui/icons-material/ContactPage";
import SearchIcon from "@mui/icons-material/Search";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import AddIcon from "@mui/icons-material/Add";

const challengeOptions = [
  {
    value: "all",
    label: "All challenges",
  },
  { value: "current", label: "Current challenges" },
  { value: "new", label: "New challenges" },
];

const filters = [
  {
    key: "my-challenges",
    label: "My Challenges",
    shortLabel: "My challenges",
    icon: (
      <ContactPageIcon
        sx={{
          marginRight: "0.25rem",
          fontSize: { mini: "1rem !important", sm: "1.5rem !important" },
        }}
      />
    ),
  },
  {
    key: "all-challenges",
    label: "Browse Challenges",
    shortLabel: "Browse",
    icon: (
      <SearchIcon
        sx={{
          marginRight: "0.25rem",
          fontSize: { mini: "1rem !important", sm: "1.5rem !important" },
        }}
      />
    ),
  },
  {
    key: "completed-challenges",
    label: "Past Challenges",
    shortLabel: "Past",
    icon: (
      <AssignmentTurnedInIcon
        sx={{
          marginRight: "0.25rem",
          fontSize: { mini: "1rem !important", sm: "1.5rem !important" },
        }}
      />
    ),
  },
];

const ChallengeArena = () => {
  const [sortChallenge, setSortChallenge] = useState(challengeOptions[0]);
  const [selectedFilter, setSelectedFilter] = useState(0);
  const [openGoogleModal, setOpenGoogleModal] = useState(false);
  const [fetch, setFetch] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPrevPage, setHasPrevPage] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [open, setOpen] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [allChallenges, setAllChallenges] = useState([]);
  const [keys, setKeys] = useState([]);

  const userId = UseUserId();
  const userDetails = UseUserDetails();
  const dispatch = useDispatch();
  const { _id: groupId } = UseSelectedWorkspace();
  const xsBp = useMediaQuery((theme) => theme.breakpoints.up("xs"));
  const navigate = useNavigate();

  const [filter, setFilter] = useState({
    userID: userId,
    allCompleted: false,
    allChallengeFilter: false,
  });

  const handleSortChallenge = (sort) => {
    setSortChallenge(sort);
  };

  const fetchCompanyIntegrations = async () => {
    try {
      let flag = 0;
      let integrationId = "";
      const { data } = await GET_COMPANY_INTEGRATIONS();
      const { data: appData } = await GET_APP_INTEGRATIONS();
      const { data: integData } = await GET_KPI_INTEGRATIONS_BY_DATASET();

      data.forEach((integrations) => {
        if (integrations.code === GOOGLE_BUSINESS) {
          flag = 1;
        }
      });
      appData.forEach((integrations) => {
        if (integrations.code === GOOGLE_BUSINESS) {
          integrationId = integrations._id;
        }
      });

      if (flag === 0) {
        setOpenGoogleModal(true);
      }
    } catch (error) {
      console.log(error.response);
    }
  };

  useEffect(() => {
    if (userDetails.isAdmin) {
      fetchCompanyIntegrations();
    }
  }, []);

  const triggerCloseBusinessModal = () => {
    setOpenGoogleModal(false);
  };

  const handleOpen = () => {
    navigate("/challenge-arena/create-challenge");
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpenConfirm = () => {
    setOpen(false);
    setOpenConfirm(true);
  };

  const handleCloseConfirm = () => {
    setOpenConfirm(false);
  };

  const fetchChallenges = async () => {
    setLoading(true);
    try {
      const { data } = await GET_ONE_VS_ONE_CHALLENGE({
        page,
        companyId: userDetails.companyId,
        ...filter,
        playerId: userId,
      });

      if (data) {
        setPage(data.page);
        setHasNextPage(data.hasNextPage);
        setHasPrevPage(data.hasPrevPage);
        setTotalPages(data.totalPages);
        setAllChallenges(data.docs);
        setKeys(Object.keys(data.docs));
      }
    } catch (error) {
      console.log("Challenge Arena Error", error.response);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchChallenges();
  }, [page, filter]);

  const triggerUpdateFilter = async (index) => {
    setSelectedFilter(index);
    if (index === 0) {
      setFilter({
        userID: userId,
        allCompleted: false,
        allChallengeFilter: false,
      });
    } else if (index === 1) {
      setFilter({
        allChallengeFilter: true,
        userID: null,
        allCompleted: false,
      });
    } else {
      setFilter({
        allCompleted: true,
        allChallengeFilter: false,
        userID: null,
      });
    }
  };

  const fetchChallengesWithoutLoading = async () => {
    try {
      const { data } = await GET_ONE_VS_ONE_CHALLENGE({
        page,
        userId,
        companyId: userDetails.companyId,
        ...filter,
        playerId: userId,
      });

      if (data) {
        setPage(data.page);
        setHasNextPage(data.hasNextPage);
        setHasPrevPage(data.hasPrevPage);
        setTotalPages(data.totalPages);
        setAllChallenges(data.docs);
        setKeys(Object.keys(data.docs));
      }
    } catch (error) {
      console.log("Challenge Arena Error", error.response);
    }
  };

  useEffect(() => {
    if (fetch !== 0) {
      fetchChallengesWithoutLoading();
    }
  }, [fetch]);

  const renderChallengeList = () => {
    if (loading) return <LoadingScreen />;

    if (keys.length === 0) {
      return (
        <SoftBox
          sx={{
            textAlign: "center",
            paddingY: { mini: "1.5rem", sm: "2.5rem" },
            paddingX: "1rem",
          }}
        >
          <SoftBox
            sx={{
              width: "56px",
              height: "56px",
              borderRadius: "50%",
              background: (theme) => theme.palette.primary.lightest,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 1rem",
            }}
          >
            <EmojiEventsOutlinedIcon
              sx={{ color: (theme) => theme.palette.primary.main, fontSize: "28px" }}
            />
          </SoftBox>
          <SoftTypography
            variant="h6"
            fontWeight="bold"
            sx={{ color: (theme) => theme.palette.grey[800], marginBottom: "0.5rem" }}
          >
            No challenges yet
          </SoftTypography>
          <SoftTypography
            variant="button"
            sx={{
              color: (theme) => theme.palette.grey[600],
              fontWeight: 400,
              display: "block",
              maxWidth: "320px",
              margin: "0 auto 1.25rem",
            }}
          >
            Challenges you create will show up here so you can track how your
            team is doing.
          </SoftTypography>
          <SoftButton
            variant="contained"
            color="primary"
            sx={{
              textTransform: "none",
              padding: "10px 24px",
            }}
            onClick={handleOpen}
          >
            <AddIcon sx={{ fontSize: "18px !important", marginRight: "0.35rem" }} />
            Create a challenge
          </SoftButton>
        </SoftBox>
      );
    }

    return (
      <SoftBox
        sx={{
          borderRadius: "8px",
          marginBottom: "10px",
        }}
      >
        {keys.map((key, index) => (
          <SoftBox key={key}>
            <SoftTypography
              sx={{
                color: "rgba(52, 58, 64, 1)",
                fontWeight: "400",
                fontSize: "16px",
                marginBottom: "10px",
                // The surrounding card already has its own top padding —
                // only groups after the first need this to separate them
                // from the group above.
                marginTop: index === 0 ? 0 : "20px",
              }}
            >
              {key}
            </SoftTypography>
            <Grid container spacing={3}>
              {allChallenges[key].map((challenge) => (
                <Grid
                  item
                  mini={12}
                  lg={4}
                  xxl={3}
                  key={challenge._id}
                  className="flex justify-center"
                >
                  <ChallengeBox
                    challenge={challenge}
                    setFetch={setFetch}
                    fetch={fetch}
                    handleOpenConfirm={handleOpenConfirm}
                  />
                </Grid>
              ))}
            </Grid>
          </SoftBox>
        ))}
      </SoftBox>
    );
  };

  return (
    <>
      {/* {userDetails.isAdmin ? (
        <Dialog
          open={openGoogleModal}
          onClose={triggerCloseBusinessModal}
          sx={{
            "& .MuiPaper-root": {
              width: "100%",
              maxWidth: "600px",
              // glass
              background:
                "linear-gradient(180deg, rgba(255,255,255,0.70) 0%, rgba(255,255,255,0.60) 100%)",
              boxShadow:
                "0px 24px 48px -8px rgba(16,24,40,0.18), inset 0px 1px 0px 0px rgba(255,255,255,0.7)",
              borderRadius: "24px",
              border: "1px solid rgba(255,255,255,0.6)",
              backdropFilter: "blur(18px) saturate(160%)",
            },
          }}
        >
          <GoogleBusinessModal
            triggerCloseBusinessModal={triggerCloseBusinessModal}
          />
        </Dialog>
      ) : null} */}

      {/* Page Header */}
      <SoftBox
        sx={{
          padding: { mini: "1rem 1.25rem", sm: "1.1rem 1.5rem" },
          background: (theme) => theme.palette.primary.main,
          borderRadius: (theme) => theme.borders.borderRadius.xl,
          marginY: { mini: "1rem", sm: "1.5rem" },
          marginX: { mini: "1rem", sm: "1.5rem" },
        }}
      >
        <SoftTypography
          variant="h2"
          color="white"
          fontWeight="bold"
          sx={{ marginBottom: "0.25rem", fontSize: "23px" }}
        >
          Challenge arena
        </SoftTypography>
        <SoftTypography
          variant="body2"
          sx={{ color: "rgba(255, 255, 255, 0.6)" }}
        >
          Challenge and get rewarded
        </SoftTypography>
      </SoftBox>

      {/* Page Content */}
      <SoftBox
        sx={{
          padding: { mini: "1.1rem 1.25rem", sm: "1.1rem 1.5rem" },
          background: "white",
          borderRadius: (theme) => theme.borders.borderRadius.xl,
          marginY: { mini: "1rem", sm: "1.5rem" },
          marginX: { mini: "1rem", sm: "1.5rem" },
          display: "flex",
          justifyContent: { mini: "center", sm: "space-between" },
          alignItems: { mini: "start", sm: "center" },
          flexDirection: { mini: "column", sm: "row" },
          gap: "1rem",
        }}
      >
        <SoftBox>
          <SoftTypography
            variant="h6"
            fontWeight="bold"
            sx={{ marginBottom: "0.35rem", color: (theme) => theme.palette.grey[800] }}
          >
            Choose your challenge and let the games begin
          </SoftTypography>
          <SoftTypography
            variant="button"
            sx={{ color: (theme) => theme.palette.grey[600], fontWeight: 400 }}
          >
            Test your skills and compete with your colleagues in various
            exciting challenges.
          </SoftTypography>
        </SoftBox>
        <SoftButton
          variant="contained"
          color="primary"
          sx={{
            textTransform: "none",
            padding: "13px 32px",
          }}
          onClick={handleOpen}
        >
          Create a challenge
        </SoftButton>
        <Dialog
          open={open}
          onClose={handleClose}
          sx={{
            "& .MuiPaper-root": {
              width: "100%",
              maxWidth: "600px",
              // glass
              background:
                "linear-gradient(180deg, rgba(255,255,255,0.70) 0%, rgba(255,255,255,0.60) 100%)",
              boxShadow:
                "0px 24px 48px -8px rgba(16,24,40,0.18), inset 0px 1px 0px 0px rgba(255,255,255,0.7)",
              borderRadius: "24px",
              border: "1px solid rgba(255,255,255,0.6)",
              backdropFilter: "blur(18px) saturate(160%)",
            },
          }}
        >
          <CreateChallenge
            handleOpen={handleOpen}
            open={open}
            handleClose={handleClose}
            handleOpenConfirm={handleOpenConfirm}
            setFetch={setFetch}
            fetch={fetch}
          />
        </Dialog>
        <Dialog
          open={openConfirm}
          onClose={handleCloseConfirm}
          sx={{
            "& .MuiPaper-root": {
              width: "100%",
              maxWidth: "600px",
              // glass
              background:
                "linear-gradient(180deg, rgba(255,255,255,0.70) 0%, rgba(255,255,255,0.60) 100%)",
              boxShadow:
                "0px 24px 48px -8px rgba(16,24,40,0.18), inset 0px 1px 0px 0px rgba(255,255,255,0.7)",
              borderRadius: "24px",
              border: "1px solid rgba(255,255,255,0.6)",
              backdropFilter: "blur(18px) saturate(160%)",
              padding: { mini: "1.5rem", md: "2rem" },
            },
          }}
        >
          <ChallengeConfirmation />
        </Dialog>
      </SoftBox>

      <SoftBox
        sx={{
          display: { mini: "flex", sm: "none" },
          gap: "0.5rem",
          marginX: "1rem",
          marginY: "0.75rem",
          alignItems: "stretch",
        }}
      >
        {filters.map((item, index) => (
          <SoftButton
            key={item.key}
            variant={selectedFilter === index ? "contained" : "outlined"}
            color={selectedFilter === index ? "primary" : null}
            size="small"
            sx={{
              flex: 1,
              minHeight: "38px",
              borderRadius: (theme) => `${theme.borders.borderRadius.md} !important`,
              borderColor: (theme) =>
                selectedFilter === index ? theme.palette.primary.main : theme.palette.grey[300],
              color: (theme) =>
                selectedFilter === index
                  ? `${theme.palette.white.main} !important`
                  : `${theme.palette.grey[600]} !important`,
              textTransform: "none",
              whiteSpace: "nowrap",
              padding: "0.4rem 0.35rem",
              fontSize: {
                mini: "0.65rem !important",
                xxs: "0.75rem !important",
                xs: "0.8rem !important",
              },
              "&:hover": {
                borderColor: (theme) =>
                  selectedFilter === index ? theme.palette.primary.main : theme.palette.grey[300],
              },
              "&:focus:not(:hover)": {
                borderColor: (theme) =>
                  selectedFilter === index ? theme.palette.primary.main : theme.palette.grey[300],
              },
            }}
            onClick={() => triggerUpdateFilter(index)}
          >
            {item.icon}
            {item.shortLabel}
          </SoftButton>
        ))}
      </SoftBox>

      <SoftBox
        sx={{
          background: "white",
          borderRadius: (theme) => theme.borders.borderRadius.xl,
          marginX: { mini: "1rem", sm: "1.5rem" },
          marginY: { mini: "0.75rem", sm: "1.5rem" },
          paddingY: { mini: "1.25rem", sm: "2rem" },
        }}
      >
        <SoftBox
          sx={{
            background: (theme) => theme.palette.grey[100],
            paddingY: "1rem",
            paddingX: "1.5rem",
            display: { mini: "none", sm: "flex" },
            justifyContent: "start",
            alignItems: "center",
            gap: "0.5rem",
          }}
        >
          {filters.map((item, index) => (
            <SoftButton
              key={item.key}
              variant={selectedFilter === index ? "contained" : "outlined"}
              color={selectedFilter === index ? "primary" : null}
              size={xsBp ? "medium" : "small"}
              sx={{
                borderRadius: (theme) => `${theme.borders.borderRadius.md} !important`,
                borderColor: (theme) =>
                  selectedFilter === index ? theme.palette.primary.main : theme.palette.grey[300],
                color: (theme) =>
                  selectedFilter === index
                    ? `${theme.palette.white.main} !important`
                    : `${theme.palette.grey[600]} !important`,
                textTransform: "none",
                padding: { sm: "9px 28px", md: "13px 32px" },
                "&:hover": {
                  borderColor: (theme) =>
                    selectedFilter === index ? theme.palette.primary.main : theme.palette.grey[300],
                },
                "&:focus:not(:hover)": {
                  borderColor: (theme) =>
                    selectedFilter === index ? theme.palette.primary.main : theme.palette.grey[300],
                },
              }}
              onClick={() => triggerUpdateFilter(index)}
            >
              {item.icon}
              {item.label}
            </SoftButton>
          ))}
        </SoftBox>
        {/* // Commented out this block since it has no clear functionality atm
        <SoftBox
          sx={{
            display: "flex",
            alignItems: "center",
            // Make the justifyContent - space between if soft select under is enabled/commented out
            justifyContent: "end",
          }}
        >
          <SoftSelect
            name="challenge"
            id="challenge"
            options={challengeOptions}
            defaultValue={{
              value: sortChallenge.value,
              label: sortChallenge.label,
            }}
            onChange={handleSortChallenge}
          />
          <SoftButton
            variant="outlined"
            sx={{
              borderColor: "#CED4DA",
              color: "#6C757D !important",
              textTransform: "none",
              padding: "13px 32px",
              "&:hover": { borderColor: "#CED4DA" },
              "&:focus:not(:hover)": {
                borderColor: "#CED4DA",
              },
            }}
          >
            View Past challenges
          </SoftButton>
        </SoftBox> */}

        <Divider
          sx={{ marginY: "2rem", display: { mini: "none", sm: "auto" } }}
        />

        <SoftBox sx={{ paddingX: { mini: "1rem", sm: "1.5rem" } }}>
          {renderChallengeList()}
          {keys.length > 0 && (
            <SoftBox
              sx={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: "0.75rem",
                marginTop: { mini: "0.5rem", sm: "1rem" },
              }}
            >
              <SoftTypography sx={{ color: (theme) => theme.palette.grey[600], fontSize: "14px" }}>
                Page{" "}
                <span
                  style={{
                    border: "1px solid #6C757D",
                    padding: "5px 10px",
                    borderRadius: "4px",
                  }}
                >
                  {page}
                </span>{" "}
                of {totalPages}
              </SoftTypography>
              <SoftBox sx={{ display: "flex", gap: "10px" }}>
                <SoftButton
                  variant="outlined"
                  size="small"
                  disabled={!hasPrevPage}
                  onClick={() => setPage(page - 1)}
                  sx={{
                    border: "0.7px solid #6C757D !important",
                    color: (theme) => `${theme.palette.grey[800]} !important`,
                  }}
                >
                  Prev
                </SoftButton>
                <SoftButton
                  variant="outlined"
                  size="small"
                  disabled={!hasNextPage}
                  onClick={() => setPage(page + 1)}
                  sx={{
                    border: "0.7px solid #6C757D !important",
                    color: (theme) => `${theme.palette.grey[800]} !important`,
                  }}
                >
                  Next
                </SoftButton>
              </SoftBox>
            </SoftBox>
          )}
        </SoftBox>
      </SoftBox>
    </>
  );
};

export default ChallengeArena;
